import { Box, Flex, Spinner, Text } from '@chakra-ui/react';
import {
  faAngleDown,
  faCircleExclamation
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQuery } from '@tanstack/react-query';
import {
  Banner,
  Button,
  FormFeedback,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  OutlineCard,
  useToast
} from '@morressier/alembic';
import React, { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { removeDraftSubmission } from 'src/api/mutations';
import {
  fetchFormSchema,
  fetchSubmissionContext,
  IFetchSubmissionContext
} from 'src/api/queries';
import { FieldLabel } from 'src/components/Field';
import { ISubmissionFormData, useFormContext } from 'src/contexts/FormContext';
import useLocalStorage from 'src/hooks/useLocalStorage';
import { UserToken } from '@publishing-engine/domain/models/user';
import { AUTH_BEARER_TOKEN_KEY } from 'src/constants';
import {
  useBlocker,
  useNavigate,
  useParams,
  useSearchParams
} from 'react-router-dom';
import { generateId } from '@publishing-engine/domain/utils/ulid';
import E2E_TEST_SELECTORS from '../../../../../e2e/test-selectors';

import {
  FormFieldArticleType,
  FormFieldCheckbox as ICheckboxFormField,
  FormFieldInfoText,
  FormFieldManuscript,
  FormFieldTitlePage,
  FormFieldArticleSubtype,
  FormFieldSelect
} from '@publishing-engine/domain/types/article';
import MissingInformationActionBanner from './MissingInformationActionBanner';
import Loading from 'src/pages/Status/Loading';
import {
  CustomFormFieldComponent,
  FormFieldCheckbox,
  FormFieldSingleFileUpload
} from '../../../../../components/FormField';
import { getFieldConfig } from './question-components/utils';
import { generateValidationSchema } from '../../../../../utils/validation';
import { ajvResolver } from '@hookform/resolvers/ajv';
import { SystemDefaults } from '@publishing-engine/submission-service/src/utils/form-field-definition-builder/form-field-definition-builder.system-defaults';
import _ from 'lodash';
import { IFormSchema } from '@publishing-engine/domain/models/form-schema';
import { useAuthorRequiredMissingFields } from 'src/hooks/useAuthorRequiredMissingFields';
import { SubmissionFlowLayout } from './components/SubmissionFlowLayout';
import { scrollToFirstError } from 'src/utils/scrollToFirstError';
import Footer from '../../../../../components/Footer';
import FormFieldTextInputComponent from 'src/components/FormField/FormFieldText';
import DeleteDraftModal from './DeleteDraftModal';
import {
  SUBMISSION_UPLOAD_LEAVE_EVENT,
  SubmissionUploadLeaveDetail
} from 'src/utils/submissionUploadLeave';
import { shouldBypassSubmissionNavigationPrompt } from './SubmissionLeaveGuard';
import { fetchUserProfileData as fetchUserProfileDetails } from 'src/api/signUpMutation';
import ScandinavianKeywordProfileOverlay from 'src/components/UserRegistrationSteps/StepThree/ScandinavianKeywordProfileOverlay';

export type IConfigs = {
  ManuscriptType: FormFieldArticleType;
  Manuscript?: FormFieldManuscript | null;
  TitlePage?: FormFieldTitlePage | null;
  TitlePageSampleDownload?: FormFieldInfoText | null;
  ExtractionSkipCheckbox?: ICheckboxFormField | null;
  ArticleLanguage?: FormFieldSelect<'metadata.articleLanguage'> | null;
  ArticleSubtype?: FormFieldArticleSubtype;
};

interface Props {
  goToNextStep: () => void;
  goToFirstStepManual: () => void;
  setDraftSubmissionId: (draftSubmissionId: string) => void;
  autosaveDraftSubmission: (
    formData: ISubmissionFormData,
    submissionId: string
  ) => Promise<{
    response: { isSuccess: boolean };
    data: {
      submissionId: string;
    };
  }>;
}

const ScandinavianKeywordsRequiredModal = ({
  isOpen,
  onClose,
  onUpdateProfile
}: {
  isOpen: boolean;
  onClose: () => void;
  onUpdateProfile: () => void;
}) => (
  <Modal isOpen={isOpen} onClose={onClose} width="520px">
    <Flex direction="column" gap={3} padding={6}>
      <Flex alignItems="center" justifyContent="space-between" gap={3}>
        <Flex alignItems="center" gap={2}>
          <Box color="actionSecondaryCautionContent">
            <FontAwesomeIcon icon={faCircleExclamation} />
          </Box>
          <Text textStyle="headingGroup">Please update your profile</Text>
        </Flex>
      </Flex>
      <Text textStyle="textContext">
        To proceed with your submission to this journal, please ensure your
        profile includes the required Scandinavian keywords. Without these, the
        submission cannot be processed.
      </Text>
      <Text textStyle="textContext">
        Please update your profile. You will be redirected back to this page to
        resume your submission once your profile is updated.
      </Text>
      <Flex justifyContent="flex-start">
        <Button variant="primary" onClick={onUpdateProfile}>
          Update Profile
        </Button>
      </Flex>
    </Flex>
  </Modal>
);

const requiresSpecialtyAndScandinavianKeyword = (
  requiredUserProfileData?: Array<{ fieldName: string; fieldType: string }>
) => {
  const requiredFields = new Set(
    requiredUserProfileData?.map(field => field.fieldName) ?? []
  );

  return (
    requiredFields.has('specialtyName') &&
    requiredFields.has('scandinavianKeyword')
  );
};

const UploadStepForm: React.FC<{
  submissionContext: IFetchSubmissionContext;
  onSelectArticleType: (articleWorkflowId: string) => void;
  configs: IConfigs;
  defaultValues?: Partial<ISubmissionFormData>;
  onSubmit: (data: ISubmissionFormData) => Promise<void>;
  submissionId: string;
}> = ({
  onSelectArticleType,
  submissionContext,
  configs,
  defaultValues,
  onSubmit,
  submissionId
}) => {
  const schema = generateValidationSchema(configs);
  const methods = useForm<ISubmissionFormData>({
    resolver: ajvResolver(schema),
    defaultValues
  });

  const { register, handleSubmit, formState, setValue, clearErrors, watch } =
    methods;
  const { errors } = formState;

  const articleTypes = submissionContext?.articleTypes;

  const articleWorkflowId = watch('submissionContext.articleWorkflowId');
  const manuscript = watch('fileSet.Manuscript');
  const titlePage = watch('fileSet.TitlePage');

  const handleValidSubmit = async (data: ISubmissionFormData) => {
    await onSubmit(data);
  };

  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(handleValidSubmit, scrollToFirstError)}>
          <SubmissionFlowLayout width="100%">
            <Box
              width="100%"
              maxWidth="960px"
              mx="auto"
              px={{ base: 3, md: 0 }}
            >
              <Flex gap={4} flexDirection="column">
                <Flex flexDirection="column" gap={2}>
                  <Text textStyle="headingTitle">
                    {
                      submissionContext?.config?.cms?.authorSubmissionForm
                        ?.startSubmissionForm?.header
                    }
                  </Text>
                  <Text textStyle="headingGroup">
                    {submissionContext?.data.name}
                  </Text>
                  {submissionContext?.data.description && (
                    <Text textStyle="textContext">
                      {submissionContext?.data.description}{' '}
                      <Link
                        variant="secondary"
                        href={
                          submissionContext.config.reviewInformationLink ?? ''
                        }
                        target="_blank"
                      >
                        View journal guidelines
                      </Link>
                    </Text>
                  )}
                </Flex>
                {submissionContext?.data?.focusIssues &&
                  submissionContext?.data?.focusIssues?.filter(
                    item => item.status === 'open'
                  )?.length > 0 && (
                    <Banner
                      variant="attention"
                      heading=""
                      content={
                        <>
                          <Flex
                            flexDirection={{ base: 'column', md: 'row' }}
                            alignItems={{ base: 'flex-start', md: 'center' }}
                            gap={1}
                          >
                            <Text>
                              {' '}
                              {`${
                                submissionContext?.data?.focusIssues?.filter(
                                  item => item.status === 'open'
                                )?.length ?? '0'
                              } Special Issues are accepting submissions`}
                            </Text>
                            {submissionContext?.data?.guidelineLink && (
                              <>
                                <Text>, view the guidelines</Text>
                                <Link
                                  variant="primary"
                                  href={submissionContext?.data?.guidelineLink}
                                  target="_blank"
                                >
                                  here
                                </Link>
                              </>
                            )}
                          </Flex>
                        </>
                      }
                    />
                  )}
                {configs.ManuscriptType && (
                  <OutlineCard width="100%">
                    <Flex flexDirection="column" padding={4} gap={4}>
                      <FieldLabel
                        label={configs.ManuscriptType.params.labels.main}
                        subText={configs.ManuscriptType.params.labels.subtitle}
                        isRequired={
                          configs.ManuscriptType.params.validations.required
                        }
                        helpText={configs.ManuscriptType.params.labels.helpText}
                      />
                      <Box
                        position="relative"
                        width={{ base: '100%', md: '50%' }}
                        maxWidth="100%"
                      >
                        <Menu
                          {...register('submissionContext.articleWorkflowId')}
                          ariaLabel={
                            E2E_TEST_SELECTORS.SUBMISSION_UPLOAD_ARTICLE_TYPE
                          }
                          matchWidth={true}
                        >
                          <MenuButton
                            variant="outline"
                            rightIcon={<FontAwesomeIcon icon={faAngleDown} />}
                            justifyContent="space-between"
                            width="100%"
                            outline={'1px solid'}
                            backgroundColor={'white'}
                            as={Button}
                            name={
                              E2E_TEST_SELECTORS.SUBMISSION_UPLOAD_ARTICLE_TYPE
                            }
                            outlineColor={errors.type && 'errorPrimaryBorder'}
                          >
                            <Text textStyle="textBody">
                              {articleTypes.find(
                                item =>
                                  item.articleWorkflowId === articleWorkflowId
                              )?.name || 'Select a Manuscript Type'}
                            </Text>
                          </MenuButton>
                          <MenuList
                            maxH="240px"
                            overflowY="auto"
                            position="relative"
                            zIndex={10}
                          >
                            {articleTypes.map(articleType => (
                              <MenuItem
                                onClick={() => {
                                  setValue(
                                    'submissionContext.articleWorkflowId',
                                    articleType.articleWorkflowId
                                  );
                                  onSelectArticleType(
                                    articleType.articleWorkflowId
                                  );
                                  clearErrors(
                                    'submissionContext.articleWorkflowId'
                                  );
                                }}
                                ariaLabel={articleType.name}
                                key={articleType.articleWorkflowId}
                                description={articleType.description}
                              >
                                <Text textStyle="headingLine">
                                  {articleType.name}
                                </Text>
                              </MenuItem>
                            ))}
                          </MenuList>
                        </Menu>
                      </Box>

                      {errors.type ? (
                        <FormFeedback variant="error">
                          Please select a Manuscript Type
                        </FormFeedback>
                      ) : null}
                    </Flex>
                  </OutlineCard>
                )}

                {configs.ArticleSubtype && (
                  <OutlineCard width="100%" padding={4}>
                    <Flex
                      flexDirection="column"
                      gap={4}
                      width={{ base: '100%', md: '50%' }}
                      mb={2}
                    >
                      <FormFieldTextInputComponent
                        config={configs.ArticleSubtype}
                        placeholder="Article subtype"
                        isEditingAllowed={true}
                        ariaLabel="Article Subtype"
                      />
                    </Flex>
                  </OutlineCard>
                )}

                {configs.ArticleLanguage && (
                  <OutlineCard width="100%" padding={4}>
                    <Flex
                      flexDirection="column"
                      gap={4}
                      width={{ base: '100%', md: '50%' }}
                      mb={2}
                    >
                      <CustomFormFieldComponent
                        config={configs.ArticleLanguage}
                        placeholder="Please enter article language"
                      />
                    </Flex>
                  </OutlineCard>
                )}
                {configs.Manuscript && (
                  <OutlineCard padding={4}>
                    <Flex flexDirection="column" gap={4}>
                      <FormFieldSingleFileUpload
                        submissionContextId={submissionContext?.id!}
                        config={configs.Manuscript}
                        methods={methods}
                        submissionId={submissionId}
                        width={{ base: '100%', md: '50%' }}
                      />
                    </Flex>
                  </OutlineCard>
                )}
                {configs.TitlePage && (
                  <OutlineCard padding={4}>
                    <Flex flexDirection="column" gap={4}>
                      <FormFieldSingleFileUpload
                        submissionContextId={submissionContext?.id!}
                        config={configs.TitlePage}
                        methods={methods}
                        submissionId={submissionId}
                        width={{ base: '100%', md: '50%' }}
                        TitlePageSampleDownload={
                          configs.TitlePageSampleDownload
                        }
                      />
                    </Flex>
                  </OutlineCard>
                )}
                {configs.ExtractionSkipCheckbox && (
                  <Flex>
                    <FormFieldCheckbox
                      config={configs.ExtractionSkipCheckbox}
                      isEditingAllowed={true}
                    />
                  </Flex>
                )}

                <Flex justifyContent="flex-start">
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={manuscript?.isUploading || titlePage?.isUploading}
                    width={{ base: '100%', md: 'auto' }}
                  >
                    Continue
                  </Button>
                </Flex>
              </Flex>
            </Box>
          </SubmissionFlowLayout>
          {/* ----- Footer Buttons ----- */}
        </form>
      </FormProvider>
      <Footer />
    </>
  );
};

const UploadStep: React.FC<Props> = ({
  goToNextStep,
  goToFirstStepManual,
  setDraftSubmissionId,
  autosaveDraftSubmission
}) => {
  const [selectedArticleType, setSelectedArticleArticleType] = React.useState<{
    name?: string;
    formSchemaId: string;
  }>({ formSchemaId: '' });
  const { ctxId = '' } = useParams();
  const [storedToken] = useLocalStorage<UserToken | null>(
    AUTH_BEARER_TOKEN_KEY,
    null
  );
  const { data: submissionContext, isLoading } = useQuery({
    queryKey: ['submission-context', ctxId],
    queryFn: () => (ctxId ? fetchSubmissionContext(ctxId) : null)
  });

  const {
    isUserDataLoading,
    isAuthorInfoMissing,
    missingFieldNames,
    userData: authorData
  } = useAuthorRequiredMissingFields(
    submissionContext?.config?.authorRequirements
  );

  const [configs, setConfigs] = React.useState<IConfigs>({
    ManuscriptType: _.set(
      SystemDefaults.ArticleType,
      'params.validations.options',
      submissionContext?.articleTypes.map(at => at.name)
    )
  });

  const {
    data: profileDetailsData,
    isLoading: isProfileDetailsLoading,
    refetch: refetchProfileDetails
  } = useQuery({
    queryKey: ['submission-upload-profile-details'],
    queryFn: fetchUserProfileDetails,
    enabled: Boolean(storedToken)
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { formData: values, updateFormData, resetFormData } = useFormContext();
  const generatedSubmissionIdRef = React.useRef(generateId());
  const [showLeaveModal, setShowLeaveModal] = React.useState(false);
  const [showScandinavianKeywordsModal, setShowScandinavianKeywordsModal] =
    React.useState(false);
  const [
    hasShownScandinavianKeywordsModal,
    setHasShownScandinavianKeywordsModal
  ] = React.useState(false);
  const [showScandinavianProfileOverlay, setShowScandinavianProfileOverlay] =
    React.useState(false);
  const removeDraftMutation = removeDraftSubmission();
  const pendingLeaveActionRef = React.useRef<(() => void) | null>(null);

  const { formSchemaId } = selectedArticleType;

  const { data: schemaData } = useQuery({
    queryKey: ['submission-context', ctxId, 'schema', formSchemaId],
    queryFn: () =>
      ctxId && formSchemaId ? fetchFormSchema(ctxId, formSchemaId) : null
  });

  useEffect(() => {
    if (!submissionContext) return;

    setConfigs(prev => ({
      ...prev,
      ManuscriptType: _.set(
        _.cloneDeep(SystemDefaults.ArticleType),
        'params.validations.options',
        submissionContext.articleTypes.map(at => at.name)
      )
    }));

    const savedArticleWorkflowId = values.submissionContext.articleWorkflowId;
    if (!savedArticleWorkflowId) return;

    const articleType = submissionContext.articleTypes.find(
      at => at.articleWorkflowId === savedArticleWorkflowId
    );

    if (!articleType) return;

    setSelectedArticleArticleType(prev =>
      prev.formSchemaId === articleType.formSchemas.authorSubmission
        ? prev
        : {
            name: articleType.name,
            formSchemaId: articleType.formSchemas.authorSubmission || ''
          }
    );
  }, [submissionContext, values.submissionContext.articleWorkflowId]);

  useEffect(() => {
    const formFields = schemaData?.documentSchema.formFields;

    if (formFields) {
      const newConfigs = {
        ManuscriptType: getFieldConfig(formFields, 'ArticleType')!,
        Manuscript: getFieldConfig(formFields, 'Manuscript'),
        TitlePage: getFieldConfig(formFields, 'TitlePage'),
        TitlePageSampleDownload: getFieldConfig(
          formFields,
          'TitlePageSampleDownload'
        ),
        ExtractionSkipCheckbox: getFieldConfig(
          formFields,
          'ExtractionSkipCheckbox'
        ),
        ArticleLanguage: getFieldConfig(formFields, 'ArticleLanguage'),
        ArticleSubtype: getFieldConfig(formFields, 'ArticleSubtype')
      } as IConfigs;

      setConfigs(newConfigs);
    }
  }, [schemaData?.documentSchema.formFields]);

  useEffect(() => {}, [schemaData]);

  const toast = useToast();

  const onSelectArticleType = (articleWorkflowId: string) => {
    const articleType = submissionContext?.articleTypes.find(
      at => at.articleWorkflowId === articleWorkflowId
    );

    if (!articleType) return;

    updateFormData({
      type: articleType.name,
      submissionContext: {
        submissionContextId: ctxId,
        organizationId: submissionContext?.organizationId!,
        articleWorkflowId: articleType.articleWorkflowId,
        schema: {} as IFormSchema,
        schemaId: articleType.formSchemas.authorSubmission,
        articleTypeCategoryId: articleType.articleTypeCategoryId,
        documentType: articleType.name
      }
    });

    setSelectedArticleArticleType({
      name: articleType?.name,
      formSchemaId: articleType?.formSchemas.authorSubmission || ''
    });
  };

  const login = () => {
    navigate('/login');
  };

  const handleUpdateProfile = () => {
    setShowScandinavianKeywordsModal(false);
    setShowScandinavianProfileOverlay(true);
  };

  const draftIdFromUrl = searchParams.get('draftId') || '';
  const submissionId = draftIdFromUrl || generatedSubmissionIdRef.current;
  const hasUploadStepData = Boolean(
    values.type ||
      values.fileSet.Manuscript?.url ||
      values.fileSet.TitlePage?.url ||
      values.submissionContext.articleWorkflowId
  );
  const specialtyAndKeywords =
    profileDetailsData?.data?.researchFocusAndExpertise?.specialtyAndKeywords ??
    [];
  const hasMissingScandinavianKeyword = specialtyAndKeywords.some(item =>
    Boolean(
      (item?.specialtyName || item?.specialtyId) && !item?.scandinavianKeyword
    )
  );
  const shouldValidateScandinavianKeywords =
    requiresSpecialtyAndScandinavianKeyword(
      submissionContext?.config?.userProfileConfig?.requiredUserProfileData
    );
  const shouldRequireScandinavianKeywords =
    shouldValidateScandinavianKeywords && hasMissingScandinavianKeyword;
  const shouldWarnBeforeLeaving =
    Boolean(draftIdFromUrl) && hasUploadStepData && Boolean(storedToken);

  useEffect(() => {
    if (
      shouldRequireScandinavianKeywords &&
      !hasShownScandinavianKeywordsModal
    ) {
      setShowScandinavianKeywordsModal(true);
      setHasShownScandinavianKeywordsModal(true);
      return;
    }

    if (!shouldRequireScandinavianKeywords) {
      setShowScandinavianKeywordsModal(false);
      setHasShownScandinavianKeywordsModal(false);
    }
  }, [hasShownScandinavianKeywordsModal, shouldRequireScandinavianKeywords]);
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      shouldBypassSubmissionNavigationPrompt() ||
      (shouldWarnBeforeLeaving &&
        currentLocation.pathname !== nextLocation.pathname)
  );

  useEffect(() => {
    if (blocker.state === 'blocked') {
      pendingLeaveActionRef.current = () => blocker.proceed();
      setShowLeaveModal(true);
    }
  }, [blocker.state]);

  useEffect(() => {
    const handleLeaveRequest = (event: Event) => {
      const customEvent = event as CustomEvent<SubmissionUploadLeaveDetail>;
      const nextAction = customEvent.detail?.action;

      customEvent.preventDefault();

      if (!shouldWarnBeforeLeaving) {
        nextAction?.();
        return;
      }

      pendingLeaveActionRef.current = nextAction ?? null;
      setShowLeaveModal(true);
    };

    window.addEventListener(
      SUBMISSION_UPLOAD_LEAVE_EVENT,
      handleLeaveRequest as EventListener
    );

    return () => {
      window.removeEventListener(
        SUBMISSION_UPLOAD_LEAVE_EVENT,
        handleLeaveRequest as EventListener
      );
    };
  }, [shouldWarnBeforeLeaving]);

  const onCloseLeaveModal = () => {
    setShowLeaveModal(false);
    pendingLeaveActionRef.current = null;
    if (blocker.state === 'blocked') {
      blocker.reset();
    }
  };

  const onConfirmLeave = async () => {
    if (!ctxId || !draftIdFromUrl) {
      onCloseLeaveModal();
      return;
    }

    const res = await removeDraftMutation.mutateAsync({
      submissionContextId: ctxId,
      submissionId: draftIdFromUrl
    });

    if (res?.isSuccess) {
      resetFormData();
      setDraftSubmissionId('');
      setShowLeaveModal(false);
      const nextAction = pendingLeaveActionRef.current;
      pendingLeaveActionRef.current = null;

      const nextUrl = new URL(window.location.href);
      nextUrl.searchParams.delete('draftId');
      window.history.replaceState({}, '', nextUrl.toString());

      nextAction?.();
      return;
    }

    toast({
      variant: 'error',
      title: 'Draft Delete Unsuccessful',
      description:
        'We encountered an issue while deleting your draft submission. Please try again.',
      isClosable: true
    });
  };

  const onSubmit = async (data: any) => {
    if (shouldRequireScandinavianKeywords) {
      setShowScandinavianKeywordsModal(true);
      return;
    }

    const updated = _.set(
      data,
      'submissionContext.schema',
      schemaData?.documentSchema
    );
    const isExtractionSkipped = updated.metadata.extractionSkipCheckbox;

    const currentUserData = {
      ...authorData?.data.user,
      affiliation: authorData?.data.affiliationDetails.organizationName,
      ringgoldId: authorData?.data.affiliationDetails?.ringgoldId
    };
    let finalData = updated;
    if (isExtractionSkipped && currentUserData) {
      finalData = {
        ...finalData,
        metadata: {
          ...finalData.metadata,
          authors: [
            {
              ...currentUserData,
              city: authorData?.data.affiliationDetails.city,
              country: authorData?.data.affiliationDetails.country,
              countryCode: authorData?.data.affiliationDetails.countryCode
            }
          ]
        },
        submittingAuthor: currentUserData?.email,
        ...(finalData.correspondingAuthor
          ? { correspondingAuthor: finalData.correspondingAuthor }
          : { correspondingAuthor: currentUserData.email })
      };
    }
    updateFormData(finalData);
    const draftToSave = { ...values, ...updated };
    const titlePageUrl = draftToSave.fileSet?.TitlePage?.url;
    draftToSave.fileSet = titlePageUrl
      ? draftToSave.fileSet
      : (({ TitlePage: _TitlePage, ...fileSet }) => fileSet)(
          draftToSave.fileSet
        );
    draftToSave.metadata.title ||= 'Untitled';
    const result = await autosaveDraftSubmission(draftToSave, submissionId);
    if (!result.response.isSuccess) {
      toast({
        variant: 'error',
        title: 'Error saving draft'
      });
    }
    setSearchParams(new URLSearchParams({ draftId: submissionId }));
    setDraftSubmissionId(submissionId);
    isExtractionSkipped ? goToFirstStepManual() : goToNextStep();
  };

  if (isLoading)
    return (
      <Flex alignItems="center" justifyContent="center" minHeight="240px">
        <Spinner />
      </Flex>
    );
  if (!submissionContext) return <div>Submission context not found</div>;

  return (
    <Flex backgroundColor="surfaceSecondaryFill" flex={1}>
      <Box width="100%">
        {storedToken ? (
          <>
            {isUserDataLoading || isProfileDetailsLoading ? (
              <Loading />
            ) : authorData?.data && isAuthorInfoMissing ? (
              <MissingInformationActionBanner
                missingFieldNames={missingFieldNames}
              />
            ) : (
              <>
                <UploadStepForm
                  key={formSchemaId || values.submissionContext.schemaId}
                  submissionContext={submissionContext}
                  onSelectArticleType={onSelectArticleType}
                  configs={configs}
                  defaultValues={values}
                  onSubmit={onSubmit}
                  submissionId={submissionId}
                />
                <ScandinavianKeywordsRequiredModal
                  isOpen={showScandinavianKeywordsModal}
                  onClose={() => setShowScandinavianKeywordsModal(false)}
                  onUpdateProfile={handleUpdateProfile}
                />
                <ScandinavianKeywordProfileOverlay
                  isOpen={showScandinavianProfileOverlay}
                  submissionContextId={ctxId}
                  onClose={() => setShowScandinavianProfileOverlay(false)}
                  onSaved={async () => {
                    await refetchProfileDetails();
                    setHasShownScandinavianKeywordsModal(false);
                  }}
                />
                <DeleteDraftModal
                  isOpen={showLeaveModal}
                  onClose={onCloseLeaveModal}
                  onRemove={onConfirmLeave}
                  isLoading={removeDraftMutation.isLoading}
                />
              </>
            )}
          </>
        ) : (
          <Flex
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            gap={4}
            minHeight={{ base: '220px', md: '320px' }}
            px={4}
          >
            <Text textStyle="headingGroup" textAlign="center">
              Please sign in to upload a submission to{' '}
              {submissionContext?.data.name}
            </Text>
            <Button onClick={login} width={{ base: '100%', sm: 'auto' }}>
              Login
            </Button>
          </Flex>
        )}
      </Box>
    </Flex>
  );
};

export default UploadStep;
